import {
  ListSubheader,
  Menu,
  useTheme,
  Theme,
  useMediaQuery,
} from '@mui/material';
import {
  CcfCloseIcon,
  CcfDivider,
  CcfTypography,
  DividerOrientation,
  DividerVariant,
} from '@nice-ccf/ui-controls';
import {
  DragDropContext,
  DraggableLocation,
  DropResult,
} from 'react-beautiful-dnd';
import { CcfDraggableColumns } from './ccf-draggable-columns';
import {
  ccfInteractionSearchActions,
  ColumnDefinitions,
  getCustomizeMenuElement,
  getDefaultColumns,
} from '../ccf-interaction-search.slice';
import { useDispatch, useSelector } from 'react-redux';
import CcfInteractionSearchStyle from '../ccf-interaction-search-styles';

export enum ColumnType {
  SELECTED_COLUMNS = 'selected-columns',
  NOT_SELECTED_COLUMNS = 'not-selected-columns',
}

/* eslint-disable-next-line */
export interface CcfInteractionSearchCustomizableMenuProps {}

/**
 * CcfInteractionSearchCustomizableMenu - to display list of interaction search columns
 * @example - `<CcfInteractionSearchCustomizableMenu />`
 */
export function CcfInteractionSearchCustomizableMenu(
  props: CcfInteractionSearchCustomizableMenuProps
) {
  const theme = useTheme();
  const anchorEl = useSelector(getCustomizeMenuElement);
  const open = Boolean(anchorEl);
  const styles = CcfInteractionSearchStyle(theme);
  const dispatch = useDispatch();
  const defaultColumns = useSelector(getDefaultColumns);
  const theme1: Theme = useTheme();

  const isSmView = useMediaQuery(theme1.breakpoints.down('xl'), {
    noSsr: true,
  });

  /**
   * function to reorder the columns list
   * @param list - array of columns
   * @param startIndex - index of column dragged
   * @param endIndex - index of column where dragged column is dropped
   * @example reorderColumns([],0,5)
   */
  const reorderColumns = (
    list: ColumnDefinitions[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  /**
   * function to move column from one list to another
   * @param source - array of columns from which one column is dragged
   * @param destination - array of columns where column is dropped
   * @param draggableSource - location information about column from source
   * @param droppableDestination - location information about column where column is dropped
   * @example moveColumns(source,destination,draggableSource,droppableDestination)
   */
  const moveColumns = (
    source: ColumnDefinitions[],
    destination: ColumnDefinitions[],
    draggableSource: DraggableLocation,
    droppableDestination: DraggableLocation
  ) => {
    if (droppableDestination.droppableId === ColumnType.SELECTED_COLUMNS) {
      if (droppableDestination.index < destination.length - 1) {
        //if column is dropped anywhere above last column
        const [removedSourceElement] = source.splice(draggableSource.index, 1);
        const [secondLastElement] = destination.splice(
          destination.length - 2,
          1
        ) as ColumnDefinitions[];
        const secondLastElementCopy = { ...secondLastElement };
        const removedElementCopy = { ...removedSourceElement };
        secondLastElementCopy.hide = true;
        removedElementCopy.hide = false;
        source.unshift(secondLastElementCopy);
        destination.splice(droppableDestination.index, 0, removedElementCopy);
      } else {
        //if column is dropped below last column
        const [removed] = source.splice(draggableSource.index, 1);
        source.unshift(removed);
      }
    } else {
      if (droppableDestination.index !== 0) {
        // when column is dropped anywhere below first column
        const [removedElement] = source.splice(draggableSource.index, 1);
        const firstElement = destination.shift() as ColumnDefinitions;
        const firstElementCopy = { ...firstElement };
        const removedElementCopy = { ...removedElement };
        firstElementCopy.hide = false;
        removedElementCopy.hide = true;
        source.splice(source.length - 2, 0, firstElementCopy);
        destination.splice(
          droppableDestination.index - 1,
          0,
          removedElementCopy
        );
      } else {
        //when column is dropped above first column
        const [removedElement] = source.splice(draggableSource.index, 1);
        source.splice(source.length - 1, 0, removedElement);
      }
    }

    if (draggableSource.droppableId === ColumnType.SELECTED_COLUMNS) {
      dispatch(
        ccfInteractionSearchActions.updateDefaultColumns([
          ...source,
          ...destination,
        ])
      );
    } else {
      dispatch(
        ccfInteractionSearchActions.updateDefaultColumns([
          ...destination,
          ...source,
        ])
      );
    }
  };

  /**
   * function when drag of column ends
   * @param result - information about the source and destination column
   * @example handleDragEnd(result)
   */
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    //if column in drag n dropped within same list of selected/not-selected columns
    if (source.droppableId === destination.droppableId) {
      const selectedColumns = [] as ColumnDefinitions[];
      const notSelectedColumns = [] as ColumnDefinitions[];
      defaultColumns.forEach((column: ColumnDefinitions) => {
        !column.hide
          ? selectedColumns.push(column)
          : notSelectedColumns.push(column);
      });
      const list =
        source.droppableId === ColumnType.SELECTED_COLUMNS
          ? selectedColumns
          : notSelectedColumns;
      const items = reorderColumns(
        list,
        source.index,
        destination.index
      ) as ColumnDefinitions[];
      if (source.droppableId === ColumnType.SELECTED_COLUMNS) {
        dispatch(
          ccfInteractionSearchActions.updateDefaultColumns([
            ...items,
            ...notSelectedColumns,
          ])
        );
      } else {
        dispatch(
          ccfInteractionSearchActions.updateDefaultColumns([
            ...selectedColumns,
            ...items,
          ])
        );
      }
    } else {
      // if column is drag n dropped from selected to not selected columns or vice-versa
      const sourceList = [] as ColumnDefinitions[];
      const destinationList = [] as ColumnDefinitions[];
      if (source.droppableId === ColumnType.SELECTED_COLUMNS) {
        defaultColumns.forEach((col: ColumnDefinitions) => {
          !col.hide ? sourceList.push(col) : destinationList.push(col);
        });
      } else {
        defaultColumns.forEach((col: ColumnDefinitions) => {
          !col.hide ? destinationList.push(col) : sourceList.push(col);
        });
      }
      moveColumns(sourceList, destinationList, source, destination);
    }
  };

  return (
    <Menu
      id="agent_interactionSearch_cases_customizableColumns_container"
      MenuListProps={{
        'aria-labelledby': 'customize-button',
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={() =>
        dispatch(ccfInteractionSearchActions.updateCustomizeMenuElement(null))
      }
      sx={{
        '& .MuiMenu-paper': {
          border: `1px solid ${theme?.palette?.text?.grey}`,

          left: isSmView ? '220px !important' : '191px',
          '&::-webkit-scrollbar': {
            width: '0.3rem',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme?.palette?.background?.scrollThumb,
            borderRadius: '2rem',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme?.palette?.background?.scrollTrack,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme?.palette?.background?.scrollThumbHover,
          },
          '& .MuiMenu-list': {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      }}
    >
      <ListSubheader
        component="div"
        id="list-subheader"
        sx={styles.listSubheader}
      >
        <CcfTypography translationKey="customize" sx={styles.customizeText} />
        <CcfCloseIcon
          viewBox="-8 -4 32 32"
          sx={styles.closeIcon}
          data-testid="close-button"
          onClick={() =>
            dispatch(
              ccfInteractionSearchActions.updateCustomizeMenuElement(null)
            )
          }
        />
      </ListSubheader>
      <DragDropContext onDragEnd={handleDragEnd}>
        <CcfDraggableColumns droppableId={ColumnType.SELECTED_COLUMNS} />
        <CcfDivider
          orientation={DividerOrientation.HORIZONTAL}
          variant={DividerVariant.FULLWIDTH}
          sx={styles.divider}
        />
        <CcfDraggableColumns droppableId={ColumnType.NOT_SELECTED_COLUMNS} />
      </DragDropContext>
    </Menu>
  );
}

export default CcfInteractionSearchCustomizableMenu;
